import { createSelector } from "reselect";
import { feedEntitiesWithGeoByTypeSelector } from "orion-components/GlobalData/Selectors";
import cloneDeep from "lodash/cloneDeep";
import { getDeviceStateHexColor } from "../DeviceState/utils";

// Function to roll up and remove duplicate command-label pairs
export const rollupCommandsWithEntities = arrays => {
	const allCommands = arrays.flat(); // Flatten the arrays into one

	// Reduce to accumulate unique commands with aggregated entities
	const uniqueCommands = allCommands.reduce((acc, current) => {
		const existingCommand = acc.find(cmd => cmd.command === current?.command && cmd?.label === current?.label);

		if (existingCommand) {
			// If the command already exists, append the entities and ensure they are unique
			existingCommand.entities = [
				...existingCommand.entities,
				...(Array.isArray(current?.entities) ? current.entities : [current.entities])
			].reduce((unique, entity) => {
				// Ensure unique entities by comparing their properties (e.g., id)
				if (!unique.some(e => e?.id === entity?.id)) {
					unique.push(entity);
				}
				return unique;
			}, []);
		} else {
			// Otherwise, add the command with entities as a flat array
			acc.push({
				...current,
				entities: Array.isArray(current?.entities) ? current?.entities : [current?.entities]
			});
		}

		return acc;
	}, []);

	return uniqueCommands;
};

// Instantiate selector factories lazily so their memoization survives store updates
// without invoking them during circular module initialization. Keep entity maps as
// inputs; Object.values here would create a new input reference on every call.
let devicesWithGeoSelector;
let camerasWithGeoSelector;

const devicesSelector = state => {
	if (!devicesWithGeoSelector) {
		devicesWithGeoSelector = feedEntitiesWithGeoByTypeSelector("device");
	}
	return devicesWithGeoSelector(state);
};

const camerasSelector = state => {
	if (!camerasWithGeoSelector) {
		camerasWithGeoSelector = feedEntitiesWithGeoByTypeSelector("camera");
	}
	return camerasWithGeoSelector(state);
};

const deviceTypeSelector = state => state?.globalData?.deviceTypes ?? {};

const compositeDeviceSelector = state => state?.globalData?.compositeDevices ?? {}; // No conversion to array

const deviceStateSelector = state => state?.globalData?.deviceState;

const getCompositeDeviceStateColorPriority = devices => {
	if (!devices || devices.length === 0) return null;
	const highPriorityDevice = devices.reduce((min, device) => {
		return device.deviceStatePriority < min.deviceStatePriority ? device : min;
	});
	return highPriorityDevice.deviceStateColor;
};

export const getAllDevicesForDataGrid = createSelector(
	[
		devicesSelector,
		camerasSelector,
		deviceTypeSelector,
		compositeDeviceSelector,
		deviceStateSelector,
		(_, searchTerm) => searchTerm, // Search term for general search
		(_, __, filter) => filter // Array of filter strings
	],
	(
		devices = {},
		cameras = {},
		deviceTypes = {},
		compositeDevices = {},
		deviceState,
		searchTerm = "",
		filter = []
	) => {
		// Combine all devices and cameras
		const deviceList = Object.values(devices);
		const cameraList = Object.values(cameras);
		const allDevices = [...deviceList, ...cameraList];

		// Process devices to separate pending devices and devices with types
		const { pendingDeviceConfigArr, devicesWithDeviceType, compositeDeviceChildren } = generateDeviceGridRows(
			allDevices,
			deviceTypes,
			compositeDevices,
			deviceState
		);

		// Add "Pending Configuration" group
		const pendingDevicesGroup = pendingDeviceConfigArr?.length
			? [{ id: "group-1", groupName: "Pending Configuration", isGroup: true }, ...pendingDeviceConfigArr]
			: [];

		// Generate composite devices data
		const compositeDevicesData = generateCompositeDevicesForDataGrid(compositeDevices, deviceTypes);

		// Group composite devices with their children
		const groupedCompositeDevices = compositeDevicesData.flatMap(compositeDevice => {
			const deviceCommands = [];
			const children = compositeDeviceChildren.filter(child => {
				if (child.group === compositeDevice.groupName) {
					child?.entityData?.properties?.commands?.forEach(command => {
						deviceCommands.push({
							...command,
							entities: cloneDeep(deviceList?.[child?.id] || cameraList?.[child?.id])
						});
					});

					return true; // Include in the result
				}
				return false; // Exclude non-matching items
			});

			compositeDevice.deviceCommands = rollupCommandsWithEntities(deviceCommands);
			compositeDevice.deviceStateColor = getCompositeDeviceStateColorPriority(children);
			return [compositeDevice, ...children]; // Group composite device with its children
		});

		// Combine all groups and devices
		let combinedDevices = [...pendingDevicesGroup, ...devicesWithDeviceType, ...groupedCompositeDevices];

		// Apply filter if provided
		if (Array.isArray(filter)) {
			const filterConditions = {
				pendingConfiguration: item =>
					item.groupName?.toLowerCase() === "pending configuration" ||
					item.deviceType?.toLowerCase() === "pending",

				configured: item =>
					(item.deviceType?.toLowerCase() !== "pending" &&
						item.groupName?.toLowerCase() !== "pending configuration") ||
					item.entityType === "compositeDevice",

				basicDevices: item =>
					item.deviceType?.toLowerCase() !== "pending" &&
					item.groupName?.toLowerCase() !== "pending configuration" &&
					!item.compositeDeviceId &&
					item.entityType !== "compositeDevice",

				compositeDevices: item => item.entityType === "compositeDevice" || item.compositeDeviceId
			};

			combinedDevices = combinedDevices.filter(item =>
				filter.some(filterTerm => {
					return filterConditions[filterTerm]?.(item);
				})
			);
		}

		// Apply search filter across all props
		const searchTermLower = searchTerm.toLowerCase();

		// Filtering out matching devices and deviceGroups with respect to the searchTerm.
		const matchingDevices = combinedDevices.filter(item => {
			const deviceName = item?.deviceName || item?.groupName || item?.entityData?.properties?.name;

			return deviceName.toString().toLowerCase().includes(searchTermLower);
		});

		// Including the deviceGroup and it's sub-devices that matches the searchTerm, ignore if already exists.
		// If deviceGroup does not match the searchTerm, but one of it's sub-device matches the searchTerm, then include the specific sub-device and its deviceGroup.
		const filteredDevices = combinedDevices?.filter(item => {
			const includeDevice = matchingDevices?.find(
				device =>
					item?.id === device?.id ||
					(item?.isGroup
						? item?.groupName === device?.group
						: device?.isGroup
							? device?.groupName === item?.group
							: null)
			);

			return includeDevice;
		});

		return filteredDevices;
	}
);

// Get value for a given field, works for both groups & devices
const getSortValue = (row, field) => {
	if (!field) {
		// default: sort by what the user sees as a name
		return (row.deviceName || row.groupName || row?.entityData?.properties?.name || "") + "";
	}

	// Special case: Device Name column for groups
	if (field === "deviceName" && row.isGroup) {
		return (row.groupName || "") + "";
	}

	// Direct prop on row
	if (row[field] !== undefined && row[field] !== null) {
		return row[field];
	}

	// Fallback: try entityData.properties
	if (row.entityData?.properties?.[field] !== undefined) {
		return row.entityData.properties[field];
	}

	return "";
};

const isNumericLike = value => {
	if (value === null || value === undefined) return false;
	const num = Number(value);
	return !isNaN(num) && isFinite(num);
};

const normalizeForSort = value =>
	String(value ?? "")
		.trim()
		.toLowerCase();

const compareValues = (a, b, direction = "asc") => {
	const dir = direction === "desc" ? -1 : 1;

	// Handle null / undefined
	if (a == null && b == null) return 0;
	if (a == null) return -1 * dir;
	if (b == null) return 1 * dir;

	// If both are numeric-like, compare as numbers
	if (isNumericLike(a) && isNumericLike(b)) {
		const na = Number(a);
		const nb = Number(b);
		if (na === nb) return 0;
		return na < nb ? -1 * dir : 1 * dir;
	}

	// Case-insensitive, "natural" string compare
	const na = normalizeForSort(a);
	const nb = normalizeForSort(b);

	if (na === nb) return 0;

	// Use localeCompare with numeric so "PTZ 2" < "PTZ 10"
	return (
		na.localeCompare(nb, undefined, {
			numeric: true,
			sensitivity: "base"
		}) * dir
	);
};

// Core: sort with grouping semantics, using whichever column is in sortModel
export const sortRowsWithGroups = (rows = [], sortModel = []) => {
	if (!rows.length) return [];

	// Use the first sort in the sortModel, else default to deviceName asc
	const activeSort = sortModel[0] || { field: "deviceName", sort: "asc" };
	const { field, sort } = activeSort;

	// Build groups: groupName -> { groupRow, children: [] }
	const groupBlocks = new Map();
	const ungroupedRows = [];

	rows.forEach(row => {
		if (row.isGroup) {
			const key = row.groupName || row.deviceName || "";
			if (!groupBlocks.has(key)) {
				groupBlocks.set(key, { groupRow: row, children: [] });
			} else {
				groupBlocks.get(key).groupRow = row;
			}
		} else if (row.group) {
			const key = row.group;
			if (!groupBlocks.has(key)) {
				groupBlocks.set(key, { groupRow: null, children: [] });
			}
			groupBlocks.get(key).children.push(row);
		} else {
			// standalone row
			ungroupedRows.push(row);
		}
	});

	// Sort children inside each group by the active field
	for (const block of groupBlocks.values()) {
		block.children.sort((a, b) => compareValues(getSortValue(a, field), getSortValue(b, field), sort));
	}

	// Build blocks: each block is a group (header + children) or a standalone row
	const blocks = [];

	groupBlocks.forEach((block, groupName) => {
		const { groupRow, children } = block;
		const representativeRow = groupRow || children[0];
		if (!representativeRow) return;

		const sortValue = getSortValue(representativeRow, field);
		const rowsInBlock = groupRow ? [groupRow, ...children] : [...children];

		blocks.push({
			sortValue,
			rows: rowsInBlock
		});
	});

	ungroupedRows.forEach(row => {
		blocks.push({
			sortValue: getSortValue(row, field),
			rows: [row]
		});
	});

	// Sort blocks by the active field
	blocks.sort((a, b) => compareValues(a.sortValue, b.sortValue, sort));

	// Flatten back into a single rows array
	return blocks.flatMap(block => block.rows);
};

//The following function retrieves devices that have device types assigned to them, as well as pending devices that do not yet have device types assigned.
const generateDeviceGridRows = (devices = [], deviceTypes = {}, compositeDevices = {}, deviceState = {}) => {
	const pendingDeviceConfigArr = [];
	const devicesWithDeviceType = [];
	const compositeDeviceChildren = [];

	devices.forEach(device => {
		const commonProperties = {
			id: device?.id,
			deviceState: device?.deviceState,
			deviceStateColor: getDeviceStateHexColor(deviceState[device?.entityData?.properties?.deviceState]?.color),
			deviceName: device?.entityData?.properties?.name || "",
			mode: device?.mode || device?.entityData?.properties?.mode || "",
			state: device?.state || device?.entityData?.properties?.state || "",
			sourceSystem: device?.sourceSystem,
			sourceName: device?.sourceName,
			sourceID: device?.sourceId,
			sourceDeviceType: device?.sourceDeviceType,
			deviceModel: device?.entityData?.properties?.deviceModel || "",
			deviceIP: device?.entityData?.properties?.deviceIP || "",
			firmware: device?.entityData?.properties?.firmware || "",
			isGroup: false,
			entityType: device?.entityType || "",
			deviceStatePriority: deviceState?.[device?.entityData?.properties?.deviceState]?.priority,
			entityData: cloneDeep(device?.entityData)
		};

		if (device?.deviceType && !device?.compositeDeviceId) {
			// Add device with deviceType
			devicesWithDeviceType.push({
				...commonProperties,
				deviceType: deviceTypes[device.deviceType]?.name,
				icon: deviceTypes[device.deviceType]?.icon,
				iconGroup: deviceTypes[device.deviceType]?.iconGroup
			});
		}

		if (device?.compositeDeviceId) {
			// Use the compositeDeviceId to look up the composite device directly
			const compositeDevice = compositeDevices[device.compositeDeviceId];
			if (compositeDevice) {
				compositeDeviceChildren.push({
					...commonProperties,
					deviceType: deviceTypes[device.deviceType]?.name,
					icon: deviceTypes[device.deviceType]?.icon,
					iconGroup: deviceTypes[device.deviceType]?.iconGroup,
					group: compositeDevice.name, // Use the name of the composite device as the group,
					compositeDeviceId: device?.compositeDeviceId
				});
			}
		}

		if (!device?.deviceType && !device?.compositeDeviceId) {
			// Add pending device
			pendingDeviceConfigArr.push({
				...commonProperties,
				deviceType: "PENDING",
				group: "Pending Configuration"
			});
		}
	});

	return { pendingDeviceConfigArr, devicesWithDeviceType, compositeDeviceChildren };
};

const generateCompositeDevicesForDataGrid = (compositeDevices, deviceTypes) => {
	const arr = [];
	Object.keys(compositeDevices).forEach(key => {
		const compositeDevice = compositeDevices[key];
		arr.push({
			id: compositeDevice?.id,
			groupName: compositeDevice?.name,
			icon: deviceTypes[compositeDevice?.deviceType]?.icon,
			iconGroup: deviceTypes[compositeDevice?.deviceType]?.iconGroup,
			isGroup: true,
			entityType: compositeDevice.entityType
		});
	});
	return arr;
};

const selectIntegrations = state => state?.session?.user?.profile?.integrations ?? [];

export const hasDevicesOrCamerasFeeds = createSelector([selectIntegrations], integrations => {
	if (integrations?.length === 0) return false;
	return integrations.some(integration => ["device", "camera"].includes(integration.entityType));
});

export const hasDeviceFeeds = createSelector([selectIntegrations], integrations => {
	if (integrations?.length === 0) return false;
	return integrations.some(integration => ["device"].includes(integration.entityType));
});
